/**
 * Patient label + attribute-key helpers.
 *
 * PHI rule: a patient has NO name. A patient's IDENTITY on screen is a stable
 * codename derived from their immutable token (see patientCodename.ts); their
 * current ward/bed is mutable PLACEMENT context, rendered from the whitelisted
 * `attributes` via the config `patients.label_format` template (e.g.
 * "{ward}-{bed}"). These helpers are the single place that turns a patient into
 * something shown on screen — never render a raw identifier.
 */

import { patientCodename } from './patientCodename';

/**
 * Render the config `label_format` template into a patient's PLACEMENT string
 * (e.g. "Medizinische Intensivstation-42"). A `{key}` placeholder is replaced
 * by `attributes[key]`, EXCEPT `{ward}`, which resolves from `wardName` — the
 * ward is the patient's PLACEMENT (current unit), not an attribute.
 * An unknown / empty key collapses to nothing, and separator runs left behind
 * by a missing value are trimmed ("B-" → "B"). Falls back to a space-joined
 * list of attribute values when the template produces nothing usable. Returns
 * '' when there is nothing to render.
 */
export function formatPatientPlacement(
  attributes: Record<string, unknown>,
  labelFormat: string | undefined,
  wardName?: string,
): string {
  if (labelFormat) {
    const rendered = labelFormat
      .replace(/\{ward\}/g, wardName ?? '')
      .replace(/\{(\w+)\}/g, (_match, key: string) => asText(attributes[key]))
      .trim();
    const cleaned = rendered.replace(/[\s\-/–]+$/u, '').replace(/^[\s\-/–]+/u, '');
    if (cleaned) return cleaned;
  }

  return Object.values(attributes)
    .map((v) => asText(v).trim())
    .filter(Boolean)
    .join(' ');
}

/**
 * One attribute value as text.
 *
 * Attribute values are TYPED on the wire — a unit may declare an integer bed
 * number or a closed set — so every place that renders one has to say what a
 * non-string becomes. Here it becomes its own printed form, and an absent or
 * cleared value becomes the empty string, which is what the template collapse
 * above already treats as "nothing to render".
 */
function asText(value: unknown): string {
  if (value === null || value === undefined) return '';
  return typeof value === 'string' ? value : String(value);
}

/**
 * A patient's primary display name: a stable, memorable codename derived from
 * their token — the same string forever, even as they move bed or ward (see
 * patientCodename.ts, which owns the codename shape). When no token is
 * available (e.g. the registration form's live preview, BEFORE the patient
 * exists), the placement string is the best label we can offer — `wardName`
 * (the selected unit's name) resolves that preview's `{ward}` placeholder.
 */
export function formatPatientLabel(
  attributes: Record<string, unknown>,
  labelFormat: string | undefined,
  patientToken?: string,
  wardName?: string,
): string {
  if (patientToken) return patientCodename(patientToken);
  return formatPatientPlacement(attributes, labelFormat, wardName) || '—';
}

/**
 * The dimmer second line under a patient's codename: their current PLACEMENT
 * (ward/bed), i.e. WHERE they are right now. Config-driven via `label_format`,
 * so it stays correct for any deployment's attribute vocabulary. `wardName`
 * (the patient's current unit name, resolved by the caller from `location_id`)
 * fills the `{ward}` placeholder. Returns '' when there is no placement to
 * show, so the caller can drop the line.
 */
export function formatPatientSecondary(
  attributes: Record<string, unknown>,
  labelFormat: string | undefined,
  wardName?: string,
): string {
  return formatPatientPlacement(attributes, labelFormat, wardName);
}

/**
 * Humanise an attribute key for use as a form-field label
 * (e.g. "date_of_birth" → "Date of birth", "wardName" → "Ward name").
 * Used when no i18n key exists for an attribute the config introduced at
 * runtime — the key set is config-driven, so labels can't be fully static.
 */
export function humaniseAttributeKey(key: string): string {
  const spaced = key
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .trim();
  if (!spaced) return key;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}
